import React from "react";
import { Icon } from "../../";
import { IntegrationActions, SetupModal } from "../";
import { useIntegrationActions } from "../../../../hooks";

const IntegrationCard = ({
  integration,
  currentIntegration,
  business,
  onBusinessUpdate,
  onIntegrationsUpdate,
}) => {
  const {
    name,
    logo,
    plugin,
    // setup_status
  } = integration;

  const {
    pluginSelected,
    loading,
    isIntegrationConnected,
    getButtonText,
    shouldHideSetupButton,
    handleButtonClick,
    handleClosePluginModal,
    handleDisconnectBusiness,
    parseTemplate,
    getVariables,
  } = useIntegrationActions({
    business,
    onBusinessUpdate,
    onIntegrationsUpdate,
  });

  // Create a modified integration object for connected state
  const getModifiedIntegration = () => {
    const isConnected = isIntegrationConnected(integration);

    if (isConnected && integration?.plugin?.links?.general_settings) {
      // When connected, swap setup_url with general_settings link
      return {
        ...integration,
        plugin: {
          ...integration.plugin,
          setup_url: integration.plugin.links.general_settings,
        },
      };
    }

    return integration;
  };

  const handleSetupButtonClick = (e) => {
    const modifiedIntegration = getModifiedIntegration();
    handleButtonClick(modifiedIntegration, e);
  };

  const isConnected = isIntegrationConnected(integration);
  const buttonText = getButtonText(integration);
  const hideButton = shouldHideSetupButton(integration);

  const hasOtherIntegrationConnected =
    currentIntegration &&
    currentIntegration.id !== integration.id &&
    (currentIntegration.setup_status === "success" ||
      (currentIntegration.enabled && currentIntegration.setup_status !== null));

  return (
    <div
      className={`hover:bg-gray-750 flex flex-col justify-between rounded-xl border border-gray-700 bg-gray-800 p-6 transition-all duration-200 hover:border-gray-600 ${
        isConnected ? "lg:col-span-2" : ""
      }`}
    >
      <div className="flex flex-col">
        {/* Header Section */}
        <div className="mb-0 flex items-start space-x-4">
          {logo && (
            <div className="flex-shrink-0">
              <img
                src={logo}
                alt={name}
                className="h-14 w-14 rounded-xl border border-gray-600 object-cover"
              />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h5 className="mb-1 text-xl font-semibold text-white">
                  {name}
                </h5>
                <p className="mb-3 text-sm text-gray-400">
                  {plugin?.description || "POS Integration"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions Section */}
      <div className="space-y-4">
        {/* Warning message when another integration is already connected */}
        {hasOtherIntegrationConnected && !isConnected && (
          <div className="rounded-lg border border-yellow-500/30 bg-yellow-900/20 p-4">
            <div className="flex items-start space-x-3">
              <Icon
                name="AlertCircle"
                className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-400"
              />
              <div className="flex-1">
                <p className="mb-1 text-sm font-medium text-yellow-300">
                  Another integration is already connected
                </p>
                <p className="text-xs text-yellow-200/80">
                  <span className="font-semibold">
                    {currentIntegration.name}
                  </span>{" "}
                  is currently connected to this store. Disconnect it to connect{" "}
                  {name}.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Show Disconnect button when another integration is connected */}
        {hasOtherIntegrationConnected && !isConnected && (
          <div className="flex justify-center">
            <button
              onClick={() =>
                handleDisconnectBusiness(currentIntegration?.id, () => {
                  onBusinessUpdate && onBusinessUpdate();
                  onIntegrationsUpdate && onIntegrationsUpdate();
                })
              }
              disabled={loading}
              className="inline-flex w-full items-center justify-center rounded-lg border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <div className="mr-2 inline h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
              ) : (
                <Icon name="Unlink" className="mr-2 inline h-4 w-4" />
              )}
              Disconnect {currentIntegration?.name}
            </button>
          </div>
        )}

        {/* Primary Action Button - Only show when NOT connected and NO other integration */}
        {!hideButton && !isConnected && !hasOtherIntegrationConnected && (
          <div className="flex justify-center">
            <button
              onClick={(e) => handleSetupButtonClick(e)}
              disabled={loading}
              className={`inline-flex w-full items-center justify-center rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                !integration?.plugin_id
                  ? "border border-transparent bg-purple-600 text-white hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:outline-none"
                  : "border border-transparent bg-green-600 text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:outline-none"
              }`}
            >
              {loading ? (
                <div className="mr-2 inline h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
              ) : (
                <Icon
                  name={!integration?.plugin_id ? "Download" : "Plus"}
                  className="mr-2 inline h-4 w-4"
                />
              )}
              {buttonText}
            </button>
          </div>
        )}

        {/* Additional Actions - Only show if connected */}
        {isConnected && (
          <div className="border-t border-gray-700 pt-4">
            <IntegrationActions
              integration={integration}
              business={business}
              parseTemplate={parseTemplate}
              onSetupClick={
                integration?.plugin?.setup_url
                  ? (e) => handleSetupButtonClick(e)
                  : null
              }
              onDisconnect={() =>
                handleDisconnectBusiness(integration?.id, () => {
                  onBusinessUpdate && onBusinessUpdate();
                  onIntegrationsUpdate && onIntegrationsUpdate();
                })
              }
              isDisconnecting={loading}
              onConfigComplete={() => {
                onBusinessUpdate && onBusinessUpdate();
                onIntegrationsUpdate && onIntegrationsUpdate();
              }}
            />
          </div>
        )}
      </div>

      {/* Setup Modal */}
      <SetupModal
        isOpen={pluginSelected.open}
        onClose={() => {
          handleClosePluginModal();
          onBusinessUpdate && onBusinessUpdate();
          onIntegrationsUpdate && onIntegrationsUpdate();
        }}
        title={pluginSelected.title}
        url={pluginSelected.url}
        parseTemplate={parseTemplate}
        variables={getVariables()}
      />
    </div>
  );
};

export default IntegrationCard;
